import { useState } from 'react';
import { RwAvatar, RwLevelPill, RwBadge, RwGlyph } from '@components/atoms';
import { RwLoading } from '@components/RwLoading';
import { RwEmpty } from '@components/RwEmpty';
import { t } from '@services/i18n';
import { useApiQuery } from '@hooks/useApiQuery';
import type { LeaderboardMember, Me } from '@shared/types';
import type { MemberScreenProps } from './types';

interface MemberProfile {
  private: boolean;
  name?: string;
  avatar?: string;
  level?: string;
  rep?: number;
  joined?: string;
  badgeCount?: number;
  badges?: Array<{ id: string; name: string; tier: string; icon: string; desc: string; rarity: string; earnedOn: string }>;
}

/**
 * Public profile of a leaderboard member, opened on row click. The endpoint is
 * STRICTLY gated server-side on the member's public_profile consent; a member
 * who has not opted in returns { private: true } and we show a private notice
 * (no badges, level or name leak).
 */
function ProfileDialog({ userId, onClose }: { userId: number; onClose: () => void }) {
  const { data, isLoading } = useApiQuery<MemberProfile>(['rw-member-profile', userId], 'memberProfile', {
    params: { user: userId },
    fallback: { private: true },
  });
  const p = data;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(20,18,12,0.45)', display: 'grid', placeItems: 'center', zIndex: 100, padding: 20 }}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'relative', width: 'min(460px, 100%)', maxHeight: '85vh', overflowY: 'auto', background: 'var(--rw-bg-card)', border: '1px solid var(--rw-line)', borderRadius: 'var(--rw-rad)', boxShadow: '0 12px 40px rgba(0,0,0,0.22)', padding: 22 }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t('COM_REWARDIFY_CLOSE', 'Close')}
          style={{ position: 'absolute', top: 12, right: 12, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--rw-fg-2)', fontSize: 16 }}
        >
          ✕
        </button>
        {isLoading ? (
          <div className="rw-fg2" style={{ padding: 30, textAlign: 'center' }}>{t('COM_REWARDIFY_LOADING', 'Loading…')}</div>
        ) : !p || p.private ? (
          <div style={{ textAlign: 'center', padding: '24px 8px' }}>
            <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'center' }}>
              <RwGlyph kind="moon" size={30} stroke="var(--rw-fg-3)" />
            </div>
            <h3 className="rw-card-t" style={{ fontSize: 18 }}>{t('COM_REWARDIFY_PROFILE_PRIVATE_TITLE', 'Profile is private')}</h3>
            <p className="rw-fg2" style={{ fontSize: 13, marginTop: 4 }}>{t('COM_REWARDIFY_PROFILE_PRIVATE_HINT', 'This member keeps their profile private.')}</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
              <RwAvatar initials={p.avatar ?? 'M'} size={56} />
              <div style={{ minWidth: 0 }}>
                <h3 className="rw-card-t" style={{ fontSize: 20, margin: 0 }}>{p.name}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  {p.level && <RwLevelPill level={p.level} />}
                  <span className="rw-fg2 rw-mono" style={{ fontSize: 12 }}>
                    {(p.rep ?? 0).toLocaleString()} {t('COM_REWARDIFY_PROFILE_REP', 'REP')}
                  </span>
                </div>
              </div>
            </div>
            <div className="rw-fg2" style={{ fontSize: 12, marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid var(--rw-line)' }}>
              {p.joined ? `${t('COM_REWARDIFY_PROFILE_MEMBER_SINCE', 'Member since %s', p.joined)} · ` : ''}
              {t('COM_REWARDIFY_PROFILE_BADGES_N', '%s badges', String(p.badgeCount ?? 0))}
            </div>
            {(p.badges?.length ?? 0) === 0 ? (
              <div className="rw-fg2" style={{ fontSize: 13, textAlign: 'center', padding: 12 }}>{t('COM_REWARDIFY_PROFILE_NO_BADGES', 'No badges earned yet.')}</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))', gap: 14 }}>
                {p.badges!.map((b) => (
                  <div key={b.id} title={b.desc} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textAlign: 'center' }}>
                    <RwBadge tier={b.tier} icon={b.icon} size={52} />
                    <span style={{ fontSize: 11, color: 'var(--rw-fg-1)', lineHeight: 1.2 }}>{b.name}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

type Period = 'week' | 'month' | 'all';

interface LeaderboardResponse {
  users: LeaderboardMember[];
  me: Me;
}

// ── Leaderboard (privacy-aware, period-based) ───────────────────────────────
export function UserLeaderboard(_props: MemberScreenProps) {
  const [period, setPeriod] = useState<Period>('month');
  // period is part of the query key so each range fetches (and caches)
  // separately; keepPrevious holds the current rows while the next loads
  // (dimmed via isFetching, or they would read as the new period's numbers).
  const { data, isLoading, isFetching } = useApiQuery<LeaderboardResponse>(['rw-leaderboard', period], 'leaderboard', {
    params: { period },
    keepPrevious: true,
    fallback: { users: [], me: undefined as unknown as Me },
  });
  const [profileId, setProfileId] = useState<number | null>(null);
  const users = data?.users ?? [];
  const me = data?.me;
  const top = users.slice(0, 10);
  // By id: a member shown by alias has no handle, and a handle is not unique
  // to a row anyway.
  const isMe = (u: LeaderboardMember): boolean => !!me && u.id === me.id;
  if (isLoading) return <RwLoading variant="table" />;
  return (
    <div className="rw-page rw-user">
      <header className="rw-page-h">
        <div>
          <div className="rw-eyebrow">{t('COM_REWARDIFY_ULEAD_EYEBROW', 'Community')}</div>
          <h1 className="rw-h1">{t('COM_REWARDIFY_ULEAD_TITLE', 'Leaderboard')}</h1>
          <p className="rw-lede">{t('COM_REWARDIFY_ULEAD_LEDE', 'Period-based and privacy-aware - members can hide themselves or show an alias.')}</p>
        </div>
        <div className="rw-toolbar">
          <div className="rw-segmented">
            <button className={period === 'week' ? 'on' : ''} onClick={() => setPeriod('week')}>{t('COM_REWARDIFY_ULEAD_PERIOD_WEEK', 'Week')}</button>
            <button className={period === 'month' ? 'on' : ''} onClick={() => setPeriod('month')}>{t('COM_REWARDIFY_ULEAD_PERIOD_MONTH', 'Month')}</button>
            <button className={period === 'all' ? 'on' : ''} onClick={() => setPeriod('all')}>{t('COM_REWARDIFY_ULEAD_PERIOD_ALL', 'All time')}</button>
          </div>
        </div>
      </header>

      {/* Mirrors .rw-page's column+gap so wrapping doesn't change spacing. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, opacity: isFetching ? 0.55 : 1, transition: 'opacity 150ms ease' }}>
      {top.length === 0 ? (
        <RwEmpty title={t('COM_REWARDIFY_ULEAD_EMPTY_TITLE', 'No leaderboard yet')} hint={t('COM_REWARDIFY_ULEAD_EMPTY_HINT', 'Once members opt in and earn reputation, they will appear here.')} />
      ) : (
      <>
      <div className="rw-podium">
        {[top[1], top[0], top[2]].map((u, i) => {
          const place = [2, 1, 3][i];
          if (!u) return null;
          return (
            <div key={u.id} className={`rw-podium-card rw-podium-${place} rw-clickable`} onClick={() => setProfileId(u.id)}>
              <div className="rw-podium-rank">{place}</div>
              <RwAvatar initials={u.avatar} size={place === 1 ? 64 : 52} />
              <div style={{ fontWeight: 600, marginTop: 8 }}>{u.name}</div>
              {u.handle && <div className="rw-fg2 rw-mono" style={{ fontSize: 11 }}>@{u.handle}</div>}
              <div className="rw-podium-points">{u.rep.toLocaleString()}</div>
              <div style={{ fontSize: 11 }} className="rw-fg2">{t('COM_REWARDIFY_ULEAD_REPUTATION', 'Reputation')}</div>
            </div>
          );
        })}
      </div>

      <div className="rw-card">
        <table className="rw-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>{t('COM_REWARDIFY_ULEAD_COL_MEMBER', 'Member')}</th>
              <th>{t('COM_REWARDIFY_ULEAD_COL_LEVEL', 'Level')}</th>
              <th style={{ textAlign: 'right' }}>{t('COM_REWARDIFY_ULEAD_COL_REPUTATION', 'Reputation')}</th>
              <th style={{ textAlign: 'right' }}>{t('COM_REWARDIFY_ULEAD_COL_BADGES', 'Badges')}</th>
            </tr>
          </thead>
          <tbody>
            {top.map((u) => (
              <tr key={u.id} className={`rw-clickable ${isMe(u) ? 'rw-row-me' : ''}`} onClick={() => setProfileId(u.id)}>
                <td className="rw-mono" style={{ fontWeight: 600 }}>{u.rank.toString().padStart(2, '0')}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <RwAvatar initials={u.avatar} size={28} />
                    <div>
                      <div style={{ fontWeight: 500 }}>{u.name} {isMe(u) && <span className="rw-fg2" style={{ fontWeight: 400 }}>{t('COM_REWARDIFY_ULEAD_YOU', '(you)')}</span>}</div>
                      {u.handle && <div className="rw-fg2 rw-mono" style={{ fontSize: 11 }}>@{u.handle}</div>}
                    </div>
                  </div>
                </td>
                <td><RwLevelPill level={u.level} /></td>
                <td className="rw-mono" style={{ textAlign: 'right', fontWeight: 600 }}>{u.rep.toLocaleString()}</td>
                <td className="rw-mono" style={{ textAlign: 'right' }}>{u.badges}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>
      )}
      </div>
      {profileId !== null && <ProfileDialog userId={profileId} onClose={() => setProfileId(null)} />}
    </div>
  );
}
